import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Tag, Popconfirm, message, Card, Tabs,
  Spin, Empty, Modal, Form, Input, Select, Typography, Tooltip, InputNumber, Dropdown,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, ReloadOutlined, BankOutlined, PrinterOutlined } from '@ant-design/icons';
import { getAccountsApi, createAccountApi, updateAccountApi, deleteAccountApi, addTransactionApi, getAccountByIdApi } from '../../api/accounts';
import { createDailyExpenseApi } from '../../api/dailyExpense';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency, escapeHtml } from '../../utils/formatters';
import { encryptData, decryptData } from '../../utils/crypto';
import dayjs from 'dayjs';

const { Title } = Typography;

const ACCOUNT_TYPES = [
  { value: 'labour',      label: 'Labour' },
  { value: 'buyer',       label: 'Buyer' },
  { value: 'shop_keeper', label: 'Vendor' },
  { value: 'general',     label: 'General' },
  { value: 'bank',        label: 'Bank Account' },
];

const TYPE_TABS = [
  { key: 'all',         label: 'All' },
  { key: 'labour',      label: 'Labour' },
  { key: 'buyer',       label: 'Buyer' },
  { key: 'shop_keeper', label: 'Vendor' },
  { key: 'general',     label: 'General' },
  { key: 'bank',        label: 'Bank' },
];

const TYPE_COLOR  = { labour: 'blue', buyer: 'green', shop_keeper: 'orange', general: 'purple', bank: 'cyan' };
const SHOW_BALANCE = new Set(['general', 'buyer', 'bank']);
const SHOW_QUICK   = new Set(['general']);

function AccountModal({ open, onClose, onSubmit, initialValues, loading }) {
  const [form] = Form.useForm();
  const typeValue = Form.useWatch('type', form);

  useEffect(() => {
    if (open) form.setFieldsValue(initialValues || { type: 'labour' });
    else form.resetFields();
  }, [open, initialValues, form]);

  const isBank = typeValue === 'bank';

  return (
    <Modal
      title={initialValues?.id ? 'Edit Account' : 'Add Account'}
      open={open} onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading}
          onClick={() => form.validateFields().then(onSubmit)}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <Form.Item label="Type" name="type" rules={[{ required: true }]}>
          <Select options={ACCOUNT_TYPES} />
        </Form.Item>
        <Form.Item label={isBank ? 'Account Nick Name' : 'Name'} name="name" rules={[{ required: true }]}>
          <Input placeholder={isBank ? 'e.g. SBI Savings' : 'Account holder name'} />
        </Form.Item>
        {!isBank && (
          <Form.Item label="Mobile" name="mobile">
            <Input placeholder="Mobile number" maxLength={15} />
          </Form.Item>
        )}
        {isBank && (
          <>
            <Form.Item label="Opening Balance (₹)" name="opening_balance" rules={[{ required: true, message: 'Enter opening balance' }]}>
              <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
            </Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item label="Bank Name" name="bank_name" rules={[{ required: true }]}>
                <Input placeholder="e.g. State Bank of India" />
              </Form.Item>
              <Form.Item label="Account Holder Name" name="account_holder_name" rules={[{ required: true }]}>
                <Input placeholder="Full name" />
              </Form.Item>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item label="Account Number" name="account_number" rules={[{ required: true }]}>
                <Input placeholder="Account number" />
              </Form.Item>
              <Form.Item label="IFSC Code" name="ifsc_code" rules={[{ required: true }]}
                normalize={(v) => v?.toUpperCase()}>
                <Input placeholder="e.g. SBIN0001234" />
              </Form.Item>
            </div>
            <Form.Item label="Note (optional)" name="note">
              <Input placeholder="Any note about this account" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}

function QuickTransactionModal({ open, onClose, onSubmit, defaultType, loading }) {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) form.setFieldsValue({ type: defaultType || 'credit', date: dayjs().format('YYYY-MM-DD') });
    else form.resetFields();
  }, [open, defaultType, form]);

  return (
    <Modal
      title={`Add ${defaultType === 'credit' ? 'Credit' : 'Debit'} Transaction`}
      open={open} onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading}
          onClick={() => form.validateFields().then(onSubmit)}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <Form.Item label="Note" name="note">
          <Input placeholder="Optional note" />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true }]}>
            <Input type="number" placeholder="0" />
          </Form.Item>
          <Form.Item label="Type" name="type" rules={[{ required: true }]}>
            <Select options={[
              { value: 'credit', label: 'Credit (Received)' },
              { value: 'debit',  label: 'Debit (Given)' },
            ]} />
          </Form.Item>
        </div>
        <Form.Item label="Date" name="date" rules={[{ required: true }]}>
          <Input type="date" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function Accounts() {
  const navigate = useNavigate();
  const { t }    = useTranslation();

  const [accounts, setAccounts]         = useState([]);
  const [bankBalances, setBankBalances] = useState({});
  const [loading, setLoading]           = useState(false);
  const [typeFilter, setTypeFilter]     = useState('all');
  const [modalOpen, setModalOpen]       = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing]           = useState(null);
  const [txnOpen, setTxnOpen]           = useState(false);
  const [txnLoading, setTxnLoading]     = useState(false);
  const [txnAccount, setTxnAccount]     = useState(null);
  const [txnType, setTxnType]           = useState('credit');

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAccountsApi();
      const accs = res.data?.data || res.data || [];
      setAccounts(accs);
      const bankAccs = accs.filter((a) => a.type === 'bank');
      Promise.all(bankAccs.map((a) => getAccountByIdApi(a.id, { type: 'bank' }).then((r) => {
        const txns = r.data?.transactions || [];
        const credit = txns.filter((t) => t.payment_status === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        const debit  = txns.filter((t) => t.payment_status === 'debit').reduce((s, t) => s + Number(t.amount), 0);
        return { id: a.id, balance: credit - debit };
      }).catch(() => ({ id: a.id, balance: null })))).then((results) => {
        setBankBalances(Object.fromEntries(results.map((r) => [r.id, r.balance])));
      });
    } catch {
      message.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const filtered = typeFilter === 'all'
    ? accounts
    : accounts.filter((a) => a.type === typeFilter);

  const handleSaveAccount = async (values) => {
    setModalLoading(true);
    try {
      let payload = values;
      if (values.type === 'bank') {
        const { bank_name, account_holder_name, account_number, ifsc_code, opening_balance, note, name } = values;
        payload = {
          name,
          type: 'bank',
          opening_balance,
          bank_details: encryptData({ bank_name, account_holder_name, account_number, ifsc_code }),
          note,
        };
      }
      editing
        ? await updateAccountApi(editing.id, payload)
        : await createAccountApi(payload);
      message.success(editing ? 'Account updated!' : 'Account added!');
      setModalOpen(false);
      setEditing(null);
      fetchAccounts();
    } catch {
      message.error('Failed to save account');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAccountApi(id);
      message.success('Account deleted');
      fetchAccounts();
    } catch {
      message.error('Failed to delete account');
    }
  };

  const handleAddTransaction = async (values) => {
    setTxnLoading(true);
    try {
      const isBank = txnAccount?.type === 'bank';
      const payload = isBank ? { ...values, account_type: 'bank' } : values;

      await addTransactionApi(txnAccount.id, payload);

      if (isBank) {
        try {
          await createDailyExpenseApi({
            title:           values.note || txnAccount.name,
            amount:          String(values.amount),
            type:            values.type === 'debit' ? 'expense' : 'income',
            date:            values.date,
            bank_account_id: txnAccount.id,
          });
        } catch {
          // daily expense creation failure should not block the transaction
        }
      }

      message.success('Transaction added!');
      setTxnOpen(false);
      fetchAccounts();
    } catch {
      message.error('Failed to add transaction');
    } finally {
      setTxnLoading(false);
    }
  };

  const openQuick = (account, type, e) => {
    e.stopPropagation();
    setTxnAccount(account);
    setTxnType(type);
    setTxnOpen(true);
  };

  const accountBalance = (r) => {
    if (!SHOW_BALANCE.has(r.type)) return null;
    if (r.type === 'bank' && bankBalances[r.id] === undefined) return null;
    return r.type === 'bank'
      ? (bankBalances[r.id] ?? 0)
      : (Number(r.total_credit) || 0) - Math.abs(Number(r.total_debit) || 0);
  };

  const handlePrint = (orientation = 'portrait') => {
    const rows = filtered.map((r, idx) => {
      const typeLabel = ACCOUNT_TYPES.find((t) => t.value === r.type)?.label || r.type;
      const bal = accountBalance(r);
      const balanceCell = bal === null
        ? '<span style="color:#9ca3af">—</span>'
        : `<span style="color:${bal >= 0 ? '#16a34a' : '#dc2626'};font-weight:600">${bal >= 0 ? '↑' : '↓'} ₹${Math.abs(bal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>`;
      return `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(r.name)}</td>
          <td>${escapeHtml(typeLabel)}</td>
          <td style="text-align:right">${balanceCell}</td>
        </tr>`;
    }).join('');

    let userName = '';
    try { userName = JSON.parse(localStorage.getItem('user'))?.name || ''; } catch { }
    const logoUrl = `${window.location.origin}/logo.png`;
    const tabLabel = TYPE_TABS.find((tb) => tb.key === typeFilter)?.label || 'All';

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Accounts Report — Kheti Hisab</title>
      <style>
        @page { size: A4 ${orientation}; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; font-size: 13px; padding: 28px 32px; color: #111; }
        .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .brand img { width: 40px; height: 40px; object-fit: contain; }
        .brand-name { font-size: 22px; font-weight: 700; color: #1a6b2a; letter-spacing: -0.3px; }
        .brand-tagline { font-size: 11px; color: #6b7280; margin-top: 1px; }
        .divider { border: none; border-top: 2px solid #1a6b2a; margin: 12px 0 16px; }
        .report-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 16px; }
        .report-title { font-size: 16px; font-weight: 700; color: #111; }
        .report-meta { font-size: 11px; color: #6b7280; text-align: right; line-height: 1.6; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #1a6b2a; color: #fff; padding: 8px 10px; text-align: left; font-size: 12px; }
        td { padding: 7px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        tbody tr:nth-child(odd) td { background: #ffffff; }
        tbody tr:nth-child(even) td { background: #eef5ec; }
        .footer { margin-top: 28px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 10px; }
        @media print { button { display: none; } }
      </style>
    </head><body>
      <div class="brand">
        <img src="${logoUrl}" alt="Kheti Hisab" onerror="this.style.display='none'" />
        <div>
          <div class="brand-name">Kheti Hisab</div>
          <div class="brand-tagline">Smart Farm Management</div>
        </div>
      </div>
      <hr class="divider" />
      <div class="report-header">
        <div class="report-title">Accounts Report — ${escapeHtml(tabLabel)}</div>
        <div class="report-meta">
          ${userName ? `<div>Account: <strong>${escapeHtml(userName)}</strong></div>` : ''}
          <div>Printed: ${dayjs().format('DD MMM YYYY, hh:mm A')}</div>
          <div>Entries: ${filtered.length}</div>
        </div>
      </div>
      <table>
        <thead><tr><th style="width:36px">Sr No.</th><th>Name</th><th>Type</th><th style="text-align:right">Balance</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">khethisab.shop &nbsp;·&nbsp; Kheti Hisab — Smart Farm Management</div>
      <script>window.onload = () => { window.print(); }<\/script>
    </body></html>`);
    win.document.close();
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: r.type === 'bank' ? '#e6fffb' : '#f0f9f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {r.type === 'bank'
              ? <BankOutlined style={{ fontSize: 16, color: '#13c2c2' }} />
              : <UserOutlined style={{ fontSize: 16, color: '#1a6b2a' }} />
            }
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}>{r.name}</div>
            {r.mobile && <div style={{ fontSize: 12, color: '#888' }}>{r.mobile}</div>}
            {r.type === 'bank' && r.bank_details && (() => {
              const dec = decryptData(r.bank_details);
              return dec ? <div style={{ fontSize: 12, color: '#888' }}>{dec.bank_name}</div> : null;
            })()}
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (v) => {
        const label = ACCOUNT_TYPES.find((t) => t.value === v)?.label || v;
        return <Tag color={TYPE_COLOR[v] || 'default'}>{label}</Tag>;
      },
    },
    {
      title: 'Balance',
      key: 'balance',
      render: (_, r) => {
        const bal = accountBalance(r);
        if (bal === null) return <span style={{ color: '#ccc' }}>—</span>;
        return (
          <span style={{ fontWeight: 700, color: bal >= 0 ? '#52c41a' : '#ff4d4f' }}>
            {bal >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(bal))}
          </span>
        );
      },
    },
    {
      title: 'Quick Actions',
      key: 'quick',
      render: (_, r) => {
        if (!SHOW_QUICK.has(r.type)) return null;
        return (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            <Button size="small" danger onClick={(e) => openQuick(r, 'debit', e)}>Debit</Button>
            <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }} onClick={(e) => openQuick(r, 'credit', e)}>Credit</Button>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                if (record.type === 'bank' && record.bank_details) {
                  const dec = decryptData(record.bank_details) || {};
                  setEditing({ ...record, ...dec });
                } else {
                  setEditing(record);
                }
                setModalOpen(true);
              }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete account?" onConfirm={() => handleDelete(record.id)} okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a' }}>👥 {t('accounts')}</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchAccounts} loading={loading} />
          <Dropdown
            disabled={filtered.length === 0}
            menu={{
              items: [
                { key: 'portrait', label: 'Print — Portrait' },
                { key: 'landscape', label: 'Print — Landscape' },
              ],
              onClick: ({ key }) => handlePrint(key),
            }}
          >
            <Tooltip title="Print accounts list">
              <Button icon={<PrinterOutlined />} disabled={filtered.length === 0} />
            </Tooltip>
          </Dropdown>
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => { setEditing(null); setModalOpen(true); }}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
            Add Account
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Card style={{ borderRadius: 10 }}>
        <Tabs activeKey={typeFilter} onChange={setTypeFilter} items={TYPE_TABS} />
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : filtered.length === 0 ? (
          <Empty description="No accounts found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            scroll={{ x: 600 }}
            onRow={(record) => ({
              onClick: () => navigate(`/accounts/${record.id}`, { state: { type: record.type, name: record.name } }),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>

      <AccountModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSubmit={handleSaveAccount}
        initialValues={editing}
        loading={modalLoading}
      />
      <QuickTransactionModal
        open={txnOpen}
        onClose={() => setTxnOpen(false)}
        onSubmit={handleAddTransaction}
        defaultType={txnType}
        loading={txnLoading}
      />
    </div>
  );
}
